import { graphql, readFragment, useMutation } from "@opensea/graphql";
import { useToasts } from "@opensea/ui-kit";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useAuthenticatedCallback } from "@/hooks/use-authenticated-callback";
import { Sound, usePlaySound } from "@/hooks/use-play-sound";
const watchCollectionMutation = graphql(`
  mutation watchCollectionMutation($slug: String!) {
    watchCollection(slug: $slug) {
      success
      error {
        __typename
      }
    }
  }
`);
const unwatchCollectionMutation = graphql(`
  mutation unwatchCollectionMutation($slug: String!) {
    unwatchCollection(slug: $slug) {
      success
      error {
        __typename
      }
    }
  }
`);
export const useToggleWatchCollectionFragment = graphql(`
  fragment useToggleWatchCollection on Collection {
    slug
    name
  }
`);
export function useToggleWatchCollection({
  collection: collectionKey,
  isWatching: isWatchingInitially
}) {
  const t = useTranslations("useToggleWatchCollection");
  const {
    slug,
    name
  } = readFragment(useToggleWatchCollectionFragment, collectionKey);
  const [isWatching, setIsWatching] = useState(isWatchingInitially);
  useEffect(() => {
    setIsWatching(isWatchingInitially);
  }, [isWatchingInitially]);
  const [playAddWatchlist] = usePlaySound(Sound.WatchlistAdd);
  const [playRemoveWatchlist] = usePlaySound(Sound.WatchlistRemove);
  const [{
    fetching: watchCollectionFetching
  }, watchCollection] = useMutation(watchCollectionMutation);
  const [{
    fetching: unwatchCollectionFetching
  }, unwatchCollection] = useMutation(unwatchCollectionMutation);
  const {
    showErrorMessage,
    showSuccessMessage
  } = useToasts();
  function getErrorMessage(error) {
    if (error === "EntryNotFoundError") {
      return t("errorEntryNotFound");
    }
    if (error === "MaxWatchListSizeError") {
      return t("errorMaxWatchListSize");
    }
    return t("errorGeneric");
  }
  const sendMutation = useDebouncedCallback(async watch => {
    try {
      let error_0;
      if (watch) {
        const result = await watchCollection({
          slug
        });
        error_0 = result.data?.watchCollection?.error?.__typename;
        if (!error_0) {
          showSuccessMessage(`Added ${name} to watchlist`);
          playAddWatchlist();
        }
      } else {
        const result_0 = await unwatchCollection({
          slug
        });
        error_0 = result_0.data?.unwatchCollection?.error?.__typename;
        if (!error_0) {
          showSuccessMessage(`Removed ${name} from watchlist`);
          playRemoveWatchlist();
        }
      }
      if (error_0) {
        const errorMessage_0 = getErrorMessage(error_0);
        showErrorMessage(errorMessage_0, {
          duration: 10_000
        });
        setIsWatching(prev_0 => !prev_0);
      }
    } catch {
      const errorMessage = t("errorGeneric");
      showErrorMessage(errorMessage, {
        duration: 10_000
      });
      setIsWatching(prev => !prev);
    }
  }, 750);
  const execute = useAuthenticatedCallback(async () => {
    const watch_0 = !isWatching;
    setIsWatching(watch_0);
    await sendMutation(watch_0);
  });
  return {
    execute,
    fetching: watchCollectionFetching || unwatchCollectionFetching,
    isWatching
  };
}